import { Dispatch, SetStateAction, useEffect, useRef, useState } from 'react';
import { CheckPointLocalStorage } from '@/entities/checkpoint';

interface CheckpointSortProps<TSchema> {
  data: TSchema[];
  filtersEmpty: boolean;
  setSortColumn: Dispatch<SetStateAction<string | null>>;
  setSortDirection: Dispatch<SetStateAction<'asc' | 'desc'>>;
}

export const useCheckpointSort = <TSchema>({
  data,
  filtersEmpty,
  setSortColumn,
  setSortDirection,
}: CheckpointSortProps<TSchema>) => {
  const previousDataRef = useRef<TSchema[] | null>(null);
  const [isOnManualSort, setIsOnManualSort] = useState(false);

  const isFromMutation =
    localStorage.getItem(CheckPointLocalStorage.IS_FROM_MUTATION) === 'true';

  const orderByUpdatedAt = () => {
    setSortColumn('updatedAt');
    setSortDirection('desc');
  };

  const orderByName = () => {
    setSortColumn('name');
    setSortDirection('asc');
  };

  /**
   * === Sorting Section ===
   */
  // useEffect(() => {
  //   // Set default sort on fresh render
  //   orderByName();
  //   // eslint-disable-next-line react-hooks/exhaustive-deps
  // }, []);

  useEffect(() => {
    if (!isOnManualSort && data) {
      // Only check for data changes after we've processed the initial data
      if (previousDataRef.current) {
        const previousData = previousDataRef.current;

        // Check if data reference changed (this happens on pagination)
        const isDifferentData = previousData !== data;

        // Check if length changed
        const hasLengthChanged =
          data.length !== previousData.length ||
          data.length > previousData.length;

        // Check if items changed by comparing first item (if exists)
        // This catches pagination where length stays same but items are different
        const hasItemsChanged =
          data.length > 0 &&
          previousData.length > 0 &&
          JSON.stringify(data[0]) !== JSON.stringify(previousData[0]);

        const shouldApplySort =
          isDifferentData &&
          (hasLengthChanged || hasItemsChanged || isFromMutation);

        if (shouldApplySort) {
          if (isFromMutation) {
            orderByUpdatedAt();
            localStorage.removeItem(CheckPointLocalStorage.IS_FROM_MUTATION);
          } else {
            // Apply name sort when filters are empty or when pagination changes
            if (filtersEmpty) {
              orderByName();
            } else {
              // When filters are applied, default to name sort
              orderByName();
            }
          }
        }
      }

      // Store current data for next comparison
      previousDataRef.current = data;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOnManualSort, data]);

  useEffect(() => {
    // Reset to default sort when filters are cleared
    if (filtersEmpty && !isFromMutation) {
      orderByName();
      // Reset the ref when filters are cleared to treat it as a fresh start
      previousDataRef.current = null;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtersEmpty]);

  const handleSort = (column: string) => {
    setIsOnManualSort((prev) => !prev);
    setSortColumn(column);
    setSortDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'));
  };
  /**
   * === END OF Sorting Section ===
   */

  return {
    handleSort,
  };
};
